import VoiceSettingsModal from "@/components/modal/VoiceSettingsModal";
import { SYNTHESIZE_SPEECH_FOR_SENTENCE } from "@/graphql/speaking.graphql";
import { withToast } from "@/lib/utils/withToast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import {
  CANVAS_DEFAULT_FONT_FAMILY,
  calculateSpeakingNavCapacity,
  clampFontScale,
  getSpeakingStyles,
} from "@ai-teach/canvas-styles";
import {
  MediaUsageContextSchema,
  type SpeakingAnswer,
  type SpeakingElement,
} from "@ai-teach/types";
import { DEFAULT_VOICE } from "@ai-teach/utils";
import { useMutation } from "@apollo/client/react";
import { useRef, useState } from "react";
import { FaMinus, FaPlus, FaSpinner, FaSyncAlt, FaTrash, FaVolumeUp } from "react-icons/fa";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { gradeChoice, gradedFillStyle } from "./gradedStyles";

interface SpeakingRendererProps {
  element: SpeakingElement;
  isLiveMode?: boolean;
  answer?: SpeakingAnswer | undefined;
  isFrozen?: boolean | undefined;
}

type SpeakingSentence = SpeakingElement["sentences"][number];

interface SynthesizeSpeechForSentenceData {
  synthesizeSpeech: { id: string; src: string; filename: string };
}
interface SynthesizeSpeechForSentenceVariables {
  text: string;
  voice?: string;
}

// Step size for the font-scale +/- buttons. clampFontScale (from canvas-styles) does the actual
// range clamping, so this only needs to be a reasonable increment.
const FONT_SCALE_STEP = 0.1;

// The add/remove-sentence buttons are fixed-size editor chrome, not part of what the student ever
// sees, so — unlike the nav dots or the stepper — they don't derive from the element's own frame.
// Fixing their size (rather than reusing the dynamically-computed nav dot size) is what makes the
// width reserved for them in the nav row deterministic and known up front, instead of depending on
// the very calculation it needs to feed into (a circular "how big are the dots" / "how much room
// is left for the dots" dependency). Tailwind's `w-7 h-7`/`gap-1` below are the literal pixel
// values these constants encode — keep them in sync if those classes ever change.
const STRUCTURAL_BUTTON_SIZE = 28;
const STRUCTURAL_BUTTON_GAP = 4;
// Gap between the nav area and the button cluster, in the row that holds both.
const NAV_ROW_GAP = 8;

function makeDefaultSentence(): SpeakingSentence {
  return {
    text: "New sentence.",
    options: [
      { text: "Option A", isCorrectAnswer: true },
      { text: "Option B", isCorrectAnswer: false },
      { text: "Option C", isCorrectAnswer: false },
      { text: "Option D", isCorrectAnswer: false },
    ],
  };
}

interface FontScaleControlProps {
  label: string;
  scale: number;
  onChange: (delta: number) => void;
}

/** Compact -/percentage/+ cluster, reused for the sentence and option scales independently. */
function FontScaleControl({ label, scale, onChange }: FontScaleControlProps) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs font-semibold text-gray-500 uppercase">{label}</span>
      <button
        type="button"
        onClick={() => onChange(-FONT_SCALE_STEP)}
        aria-label={`Decrease ${label.toLowerCase()} text size`}
        title={`Decrease ${label.toLowerCase()} text size`}
        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
      >
        <FaMinus className="w-3 h-3" />
      </button>
      <span className="text-xs font-medium text-gray-500 w-10 text-center select-none">
        {Math.round(scale * 100)}%
      </span>
      <button
        type="button"
        onClick={() => onChange(FONT_SCALE_STEP)}
        aria-label={`Increase ${label.toLowerCase()} text size`}
        title={`Increase ${label.toLowerCase()} text size`}
        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
      >
        <FaPlus className="w-3 h-3" />
      </button>
    </div>
  );
}

interface SpeakingNavStepperProps {
  currentIndex: number;
  total: number;
  fontSize: number;
  buttonSize: number;
  onPrev: () => void;
  onNext: () => void;
  canGoPrev: boolean;
  canGoNext: boolean;
}

/**
 * Fixed-width "‹ n / m ›" replacement for the dot row once there are too many sentences to show
 * one dot each (see `calculateSpeakingNavCapacity`). Its width never grows with the sentence
 * count, unlike the dots (or a scrolling row of them, rejected as a fix) — it can never overflow
 * the row or collide with the add/remove buttons again.
 */
function SpeakingNavStepper({
  currentIndex,
  total,
  fontSize,
  buttonSize,
  onPrev,
  onNext,
  canGoPrev,
  canGoNext,
}: SpeakingNavStepperProps) {
  return (
    <div className="flex items-center justify-center gap-2">
      <button
        type="button"
        onClick={onPrev}
        disabled={!canGoPrev}
        aria-label="Previous sentence"
        title="Previous sentence"
        style={{ fontSize: buttonSize, fontFamily: CANVAS_DEFAULT_FONT_FAMILY }}
        className="leading-none text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        ‹
      </button>
      <span
        style={{ fontSize, fontFamily: CANVAS_DEFAULT_FONT_FAMILY }}
        className="font-semibold text-gray-700 select-none"
      >
        {currentIndex + 1} / {total}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={!canGoNext}
        aria-label="Next sentence"
        title="Next sentence"
        style={{ fontSize: buttonSize, fontFamily: CANVAS_DEFAULT_FONT_FAMILY }}
        className="leading-none text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        ›
      </button>
    </div>
  );
}

/**
 * Authoring view: one sentence at a time (numbered dots, or a "‹ n / m ›" stepper once there are
 * too many to dot individually, to switch), its TTS play/generate button, its text, and its four
 * options with a correct-answer toggle — mirroring `FillBlanksRenderer`'s fully inline editing (no
 * separate element toolbar). TTS is generated explicitly per sentence via the speaker button,
 * never automatically on keystroke, since it costs money and latency.
 *
 * The top two rows hold every control that changes the *shape* of the element (add/remove a
 * sentence, adjust text size) — kept together and away from the sentence-text row below, which
 * is where the teacher is actually typing, so neither area steals focus or attention from the
 * other.
 */
export function SpeakingRenderer({
  element,
  isLiveMode = false,
  answer,
  isFrozen,
}: SpeakingRendererProps) {
  const dispatch = useAppDispatch();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [synthesizeSpeech, { loading: isGeneratingTts }] = useMutation<
    SynthesizeSpeechForSentenceData,
    SynthesizeSpeechForSentenceVariables
  >(SYNTHESIZE_SPEECH_FOR_SENTENCE);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isVoiceSettingsOpen, setIsVoiceSettingsOpen] = useState(false);
  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const sentences = element.sentences;
  const safeIndex = Math.max(0, Math.min(currentIndex, sentences.length - 1));
  const currentSentence = sentences[safeIndex];
  const sentenceFontScale = clampFontScale(element.sentenceFontScale);
  const optionFontScale = clampFontScale(element.optionFontScale);

  // The nav row shares its row with the add/remove-sentence buttons (below), so the width
  // available to it is the element's width *minus* that reserved, fixed-size cluster — never the
  // whole element's width. This is the fix for a real bug: computing capacity off the whole
  // element width said "fits" for rows that, in the real rendered layout, didn't, because part of
  // that width always belonged to the buttons — the dots rendered on top of them.
  const buttonCount = isLiveMode ? 0 : 1 + (sentences.length > 1 ? 1 : 0);
  const reservedButtonWidth =
    buttonCount > 0
      ? buttonCount * STRUCTURAL_BUTTON_SIZE +
        (buttonCount - 1) * STRUCTURAL_BUTTON_GAP +
        NAV_ROW_GAP
      : 0;

  const capacity = calculateSpeakingNavCapacity(element.size.width, reservedButtonWidth);
  const isCondensed = sentences.length > capacity;

  const styles = getSpeakingStyles(
    element.size.width,
    element.size.height,
    sentences.length,
    sentenceFontScale,
    optionFontScale,
    reservedButtonWidth
  );

  const updateSentences = (nextSentences: SpeakingSentence[]) => {
    dispatch(updateElementProps({ id: element.id, props: { sentences: nextSentences } }));
  };

  const handleSentenceTextChange = (text: string) => {
    updateSentences(sentences.map((s, i) => (i === safeIndex ? { ...s, text } : s)));
  };

  const handleOptionTextChange = (optionIndex: number, text: string) => {
    updateSentences(
      sentences.map((s, i) =>
        i === safeIndex
          ? { ...s, options: s.options.map((o, oi) => (oi === optionIndex ? { ...o, text } : o)) }
          : s
      )
    );
  };

  const handleToggleCorrectAnswer = (optionIndex: number) => {
    updateSentences(
      sentences.map((s, i) =>
        i === safeIndex
          ? {
              ...s,
              options: s.options.map((o, oi) => ({ ...o, isCorrectAnswer: oi === optionIndex })),
            }
          : s
      )
    );
  };

  const handleAddSentence = () => {
    updateSentences([...sentences, makeDefaultSentence()]);
    setCurrentIndex(sentences.length);
  };

  const handleRemoveSentence = () => {
    if (sentences.length <= 1) return;
    const nextSentences = sentences.filter((_, i) => i !== safeIndex);
    updateSentences(nextSentences);
    setCurrentIndex((prev) => Math.max(0, Math.min(prev, nextSentences.length - 1)));
  };

  const handleSentenceFontScaleChange = (delta: number) => {
    dispatch(
      updateElementProps({
        id: element.id,
        props: { sentenceFontScale: clampFontScale(sentenceFontScale + delta) },
      })
    );
  };

  const handleOptionFontScaleChange = (delta: number) => {
    dispatch(
      updateElementProps({
        id: element.id,
        props: { optionFontScale: clampFontScale(optionFontScale + delta) },
      })
    );
  };

  // The teacher isn't gated by single-attempt reachability the way a student is — every sentence
  // is reachable today (one click on its dot), and stepping through them sequentially keeps that
  // true, just not in one click once condensed.
  const handleStepperPrev = () => setCurrentIndex((prev) => Math.max(0, prev - 1));
  const handleStepperNext = () =>
    setCurrentIndex((prev) => Math.min(sentences.length - 1, prev + 1));

  // A single reusable audio element, matching TextElementToolbar's rationale: reusing one element
  // lets playback (after the async TTS request) stay tied to the user's click gesture.
  const getAudioEl = () => {
    if (!audioRef.current) audioRef.current = new Audio();
    return audioRef.current;
  };

  const playSrc = (src: string) => {
    const audio = getAudioEl();
    audio.src = src;
    audio.play().catch((err) => {
      console.error("[SpeakingRenderer] audio playback failed", err);
    });
  };

  const generateAndPlay = () => {
    if (!currentSentence || !currentSentence.text.trim()) return;

    withToast(
      async () => {
        const res = await synthesizeSpeech({
          variables: { text: currentSentence.text, voice: DEFAULT_VOICE },
        });

        const generated = res.data?.synthesizeSpeech;
        if (!generated?.src) throw new Error();

        updateSentences(
          sentences.map((s, i) =>
            i === safeIndex
              ? {
                  ...s,
                  audio: { src: generated.src, filename: generated.filename, id: generated.id },
                }
              : s
          )
        );

        playSrc(generated.src);
      },
      {
        loading: "Generating audio...",
        success: "Audio ready",
        error: "Failed to generate audio",
      },
      "speakingSentenceTtsToast"
    ).catch((err) => {
      console.error("[SpeakingRenderer] TTS generation failed", err);
    });
  };

  // Play the stored audio if we already have it, otherwise generate it first.
  const handleSpeak = () => {
    if (currentSentence?.audio?.src) {
      playSrc(currentSentence.audio.src);
      return;
    }
    generateAndPlay();
  };

  if (!currentSentence) return null;

  return (
    <div
      style={{
        ...styles.outerDiv,
        borderRadius: element.border.cornerRadius,
        backgroundColor: element.backgroundColor ?? "transparent",
      }}
      className="text-black"
    >
      {/* Row 1: sentence navigation (dots below capacity, a fixed-width stepper at or above it) +
          add/remove-sentence controls */}
      <div
        style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: NAV_ROW_GAP }}
      >
        <div style={{ ...styles.navRow, flex: 1, minWidth: 0 }}>
          {isCondensed ? (
            <SpeakingNavStepper
              currentIndex={safeIndex}
              total={sentences.length}
              fontSize={styles.stepperFontSize}
              buttonSize={styles.stepperButtonSize}
              onPrev={handleStepperPrev}
              onNext={handleStepperNext}
              canGoPrev={safeIndex > 0}
              canGoNext={safeIndex < sentences.length - 1}
            />
          ) : (
            sentences.map((_, index) => (
              <button
                type="button"
                // biome-ignore lint/suspicious/noArrayIndexKey: sentences are reordered only by add/remove at the end
                key={`sentence-nav-${index}`}
                style={{
                  ...styles.navDot,
                  ...(index === safeIndex ? styles.navDotActive : {}),
                  fontSize: styles.navDotFontSize,
                  fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                  flexShrink: 0,
                }}
                onClick={() => setCurrentIndex(index)}
              >
                {index + 1}
              </button>
            ))
          )}
        </div>

        {!isLiveMode && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={handleAddSentence}
              aria-label="Add sentence"
              title="Add sentence"
              className="w-7 h-7 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-50"
            >
              <FaPlus />
            </button>

            {sentences.length > 1 && (
              <button
                type="button"
                onClick={handleRemoveSentence}
                aria-label="Remove sentence"
                title="Remove sentence"
                className="w-7 h-7 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:text-red-500"
              >
                <FaTrash />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Row 2: independent sentence / option text-size controls */}
      {!isLiveMode && (
        <div className="flex items-center gap-3">
          <FontScaleControl
            label="Sentence"
            scale={sentenceFontScale}
            onChange={handleSentenceFontScaleChange}
          />
          <div className="w-px h-6 bg-gray-300" />
          <FontScaleControl
            label="Options"
            scale={optionFontScale}
            onChange={handleOptionFontScaleChange}
          />
        </div>
      )}

      {/* Current sentence: play/generate audio + text */}
      <div style={styles.sentenceContainer}>
        <button
          type="button"
          onClick={handleSpeak}
          disabled={isGeneratingTts}
          style={styles.playButton}
          aria-label={currentSentence.audio?.src ? "Play audio" : "Generate audio"}
          title={currentSentence.audio?.src ? "Play audio 播放語音" : "Generate audio 產生音檔"}
        >
          {isGeneratingTts ? (
            <FaSpinner className="animate-spin" />
          ) : (
            <FaVolumeUp color={currentSentence.audio?.src ? "#FF9433" : "#989898"} />
          )}
        </button>

        {!isLiveMode && (
          <button
            type="button"
            onClick={() => setIsVoiceSettingsOpen(true)}
            disabled={isGeneratingTts || !currentSentence.text.trim()}
            aria-label="Voice settings"
            title="Voice settings 語音設定"
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-40"
          >
            <FaSyncAlt className="w-3 h-3" />
          </button>
        )}

        <div
          contentEditable={!isLiveMode}
          data-no-moveable-drag
          suppressContentEditableWarning
          onBlur={(e) => handleSentenceTextChange(e.currentTarget.innerText)}
          style={{
            fontSize: styles.sentenceFontSize,
            fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
            flex: 1,
            outline: "none",
          }}
        >
          {currentSentence.text}
        </div>
      </div>

      {/* Options */}
      <div style={styles.optionsContainer}>
        {currentSentence.options.map((option, optionIndex) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: options are a fixed four-slot sequence, no stable id
            key={`option-${safeIndex}-${optionIndex}`}
            style={{
              ...styles.optionButton,
              fontSize: styles.optionFontSize,
              fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
              display: "flex",
              alignItems: "center",
              gap: 8,
              ...gradedFillStyle(
                gradeChoice(
                  option.isCorrectAnswer,
                  answer?.answers[`${safeIndex}`] === `${optionIndex}`,
                  isFrozen
                )
              ),
            }}
          >
            {!isLiveMode &&
              (option.isCorrectAnswer ? (
                <MdCheckBox
                  onClick={() => handleToggleCorrectAnswer(optionIndex)}
                  className="text-success-500 text-xl cursor-pointer shrink-0"
                />
              ) : (
                <MdCheckBoxOutlineBlank
                  onClick={() => handleToggleCorrectAnswer(optionIndex)}
                  className="text-slate-400 text-xl cursor-pointer shrink-0"
                />
              ))}
            <div
              contentEditable={!isLiveMode}
              data-no-moveable-drag
              suppressContentEditableWarning
              onBlur={(e) => handleOptionTextChange(optionIndex, e.currentTarget.innerText)}
              style={{ outline: "none", flex: 1 }}
            >
              {option.text}
            </div>
          </div>
        ))}
      </div>

      {isVoiceSettingsOpen && slide && !isLiveMode && (
        <VoiceSettingsModal
          audioData={{
            id: currentSentence.audio?.id ?? null,
            filename: currentSentence.audio?.filename ?? null,
            src: currentSentence.audio?.src ?? null,
          }}
          onClose={() => setIsVoiceSettingsOpen(false)}
          text={currentSentence.text}
          slideId={slide.id}
          voice={DEFAULT_VOICE}
          usageContext={MediaUsageContextSchema.Values.aiTextToSpeech}
          onSave={({ src, id, filename }) => {
            updateSentences(
              sentences.map((sentence, i) =>
                i === safeIndex ? { ...sentence, audio: { src, filename, id } } : sentence
              )
            );
            setIsVoiceSettingsOpen(false);
          }}
        />
      )}
    </div>
  );
}
